import { Mat4, Quat, Vec3 } from 'playcanvas';

import type { Annotation } from '../annotations';
import type { CameraAnimTrack, Pose } from '../camera-poses';
import { AnimTrackEditOp, EntityTransformOp, MultiOp } from '../edit-ops';
import type { Scene } from '../scene';
import { Splat } from '../splat';
import { Transform } from '../transform';

// Scale about the reference midpoint. Bake into the saved layer transforms,
// rather than a transient scene root, so exports retain the metric scale.
const createDistanceScaleOp = (scene: Scene, first: Vec3, second: Vec3, meters: number): MultiOp | null => {
    const distance = first.distance(second);
    const factor = meters / distance;
    if (!Number.isFinite(meters) || meters <= 0 || !Number.isFinite(distance) || distance <= 1e-8 ||
        !Number.isFinite(factor) || factor <= 0) {
        return null;
    }

    const midpoint = first.clone().add(second).mulScalar(0.5);
    const scalePoint = (point: Vec3, center = midpoint) => point.clone().sub(center).mulScalar(factor).add(center);
    const ops = scene.elements.filter((element): element is Splat => element instanceof Splat).map((splat) => {
        const entity = splat.entity;
        const oldt = new Transform(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
        const center = new Mat4().invert(entity.parent.getWorldTransform()).transformPoint(midpoint);
        return new EntityTransformOp({
            splat,
            oldt,
            newt: new Transform(scalePoint(oldt.position, center), oldt.rotation, oldt.scale.clone().mulScalar(factor))
        });
    });
    if (ops.length === 0) return null;

    const result = new MultiOp(ops);
    result.name = 'distanceScale';
    const { events } = scene;
    const guideOp = events.invokeOptional?.('spatialGuides.scaleOp', first, second, factor);
    // An already-calibrated scene still needs to save its reference pair.
    if (Math.abs(factor - 1) < 1e-10) return guideOp ? new MultiOp([guideOp]) : null;
    if (guideOp) result.ops.push(guideOp);

    // Camera keyframes have their own user transform. Scale in that coordinate
    // system so the existing transform and serialized poses remain consistent.
    const track: CameraAnimTrack = events.invoke('camera.animTrack');
    const before = track.snapshot();
    if (before.length) {
        const poseTransform = events.invoke('camera.poseTransform');
        const poseMatrix = new Mat4().setTRS(new Vec3(poseTransform.position),
            new Quat().setFromEulerAngles(...poseTransform.rotation as [number, number, number]),
            new Vec3(poseTransform.scale, poseTransform.scale, poseTransform.scale));
        const center = poseMatrix.invert().transformPoint(midpoint);
        const after: Pose[] = before.map(pose => ({
            ...pose,
            position: scalePoint(pose.position, center),
            target: scalePoint(pose.target, center)
        }));
        result.ops.push(new AnimTrackEditOp('distanceScaleCameras', track, before, after));
    }

    // Keep annotation identities and text intact, including edits made after
    // calibration. Only their spatial fields belong to this undo operation.
    const annotations: Annotation[] = events.invoke('annotations');
    const pack = (point: Vec3): [number, number, number] => [point.x, point.y, point.z];
    const snapshots = annotations.map((annotation) => {
        const before = {
            position: [...annotation.position] as [number, number, number],
            cameraPosition: [...annotation.camera.initial.position] as [number, number, number],
            cameraTarget: [...annotation.camera.initial.target] as [number, number, number]
        };
        const after = {
            position: pack(scalePoint(new Vec3(before.position))),
            cameraPosition: pack(scalePoint(new Vec3(before.cameraPosition))),
            cameraTarget: pack(scalePoint(new Vec3(before.cameraTarget)))
        };
        return { annotation, before, after };
    });
    const applyAnnotations = (state: 'before' | 'after') => {
        for (const snapshot of snapshots) {
            const { annotation } = snapshot;
            annotation.position = [...snapshot[state].position];
            annotation.camera.initial.position = [...snapshot[state].cameraPosition];
            annotation.camera.initial.target = [...snapshot[state].cameraTarget];
        }
        events.fire('annotations.changed', events.invoke('annotations'));
        scene.forceRender = true;
    };
    result.ops.push({
        name: 'distanceScaleAnnotations',
        do: () => applyAnnotations('after'),
        undo: () => applyAnnotations('before')
    });
    return result;
};

export { createDistanceScaleOp };
